import {
  FileRenderType,
  getFileRenderType,
} from 'apps/TenantApp/screens/ItemViewer/FileRenderer';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { useNodeSecurity_addWatermark_Mutation } from './__generated__/useNodeSecurity_addWatermark_Mutation.graphql';
import { useNodeSecurity_allowDownload_Mutation } from './__generated__/useNodeSecurity_allowDownload_Mutation.graphql';
import { useNodeSecurity_node$key } from './__generated__/useNodeSecurity_node.graphql';
import { useNodeSecurity_preventDownload_Mutation } from './__generated__/useNodeSecurity_preventDownload_Mutation.graphql';
import { useNodeSecurity_removeWatermark_Mutation } from './__generated__/useNodeSecurity_removeWatermark_Mutation.graphql';

const nodeSecurityFragment = graphql`
  fragment useNodeSecurity_node on Node {
    id
    ... on HasNodeSecurityInterface {
      canUpdateSecurity {
        value
      }
      preventDownload
      hasWatermark
    }
    ... on FolderItem {
      folderItemContent: content {
        ... on File {
          mimeType
        }
      }
    }
    ... on VaultItem {
      vaultItemContent: content {
        ... on File {
          mimeType
        }
      }
    }
    ... on VaultDraftItem {
      draftVaultItemContent: content {
        ... on File {
          mimeType
        }
      }
    }
  }
`;

const allowDownloadMutation = graphql`
  mutation useNodeSecurity_allowDownload_Mutation($nodeIds: [ID!]!) {
    allowDownload(input: { nodeIds: $nodeIds }) {
      nodes {
        ...useNodeSecurity_node
      }
    }
  }
`;

const preventDownloadMutation = graphql`
  mutation useNodeSecurity_preventDownload_Mutation($nodeIds: [ID!]!) {
    preventDownload(input: { nodeIds: $nodeIds }) {
      nodes {
        ...useNodeSecurity_node
      }
    }
  }
`;

const addWatermarkMutation = graphql`
  mutation useNodeSecurity_addWatermark_Mutation($nodeIds: [ID!]!) {
    addWatermark(input: { nodeIds: $nodeIds }) {
      nodes {
        ...useNodeSecurity_node
      }
    }
  }
`;

const removeWatermarkMutation = graphql`
  mutation useNodeSecurity_removeWatermark_Mutation($nodeIds: [ID!]!) {
    removeWatermark(input: { nodeIds: $nodeIds }) {
      nodes {
        ...useNodeSecurity_node
      }
    }
  }
`;

interface NodeSecurity {
  supportsNodeSecurity: boolean;
  canUpdateSecurity: boolean;

  supportsDownloadPrevention: boolean;
  isDownloadPrevented: boolean;
  isTogglePreventDownloadLoading: boolean;

  togglePreventDownload(syncedNodeIds?: string[]): void;

  supportsWatermark: boolean;
  hasWatermark: boolean;
  isToggleWatermarkLoading: boolean;

  toggleWatermark(syncedNodeIds?: string[]): void;
}

export function useNodeSecurity(node: useNodeSecurity_node$key): NodeSecurity {
  const {
    id,
    draftVaultItemContent,
    vaultItemContent,
    folderItemContent,
    ...security
  } = useFragment(nodeSecurityFragment, node);
  const {
    canUpdateSecurity,
    preventDownload: isDownloadPrevented,
    hasWatermark,
  } = security;
  const [allowDownload, isAllowDownloadLoading] =
    useMutation<useNodeSecurity_allowDownload_Mutation>(allowDownloadMutation);
  const [preventDownload, isPreventDownloadLoading] =
    useMutation<useNodeSecurity_preventDownload_Mutation>(
      preventDownloadMutation,
    );

  const [addWatermark, isAddWatermarkLoading] =
    useMutation<useNodeSecurity_addWatermark_Mutation>(addWatermarkMutation);
  const [removeWatermark, isRemoveWatermarkLoading] =
    useMutation<useNodeSecurity_removeWatermark_Mutation>(
      removeWatermarkMutation,
    );

  //Note: Checks if object exists, doesn't check the value of that object
  const supportsNodeSecurity = !!security.canUpdateSecurity;

  const guardNodeSecurityMutation = () => {
    if (!supportsNodeSecurity) {
      throw new Error(
        'Operation not supported. Object does not correctly implement the HasNodeSecurityInterface.',
      );
    }
    if (!canUpdateSecurity) {
      throw new Error(
        'Operation not allowed. User does not have correct permissions.',
      );
    }
  };

  const fileMimeType =
    draftVaultItemContent?.mimeType ||
    vaultItemContent?.mimeType ||
    folderItemContent?.mimeType;
  const supportsWatermark =
    getFileRenderType(fileMimeType) === FileRenderType.PDF;
  const supportsDownloadPrevention = !!fileMimeType; // only files can be downloaded right now.

  return {
    supportsNodeSecurity,
    canUpdateSecurity: !!canUpdateSecurity?.value,

    supportsWatermark: supportsWatermark && supportsNodeSecurity,
    hasWatermark: !!hasWatermark,
    isToggleWatermarkLoading: isAddWatermarkLoading || isRemoveWatermarkLoading,
    toggleWatermark: (syncedNodeIds = []) => {
      guardNodeSecurityMutation();
      if (!hasWatermark) {
        addWatermark({ variables: { nodeIds: [...syncedNodeIds, id] } });
      } else {
        removeWatermark({ variables: { nodeIds: [...syncedNodeIds, id] } });
      }
    },

    supportsDownloadPrevention:
      supportsDownloadPrevention && supportsNodeSecurity,
    isDownloadPrevented: !!isDownloadPrevented,
    isTogglePreventDownloadLoading:
      isAllowDownloadLoading || isPreventDownloadLoading,
    togglePreventDownload: (syncedNodeIds = []) => {
      guardNodeSecurityMutation();
      if (!!isDownloadPrevented) {
        allowDownload({ variables: { nodeIds: [...syncedNodeIds, id] } });
      } else {
        preventDownload({ variables: { nodeIds: [...syncedNodeIds, id] } });
      }
    },
  };
}

export function useSyncNodeSecurity() {
  const [allowDownload] = useMutation<useNodeSecurity_allowDownload_Mutation>(
    allowDownloadMutation,
  );
  const [preventDownload] =
    useMutation<useNodeSecurity_preventDownload_Mutation>(
      preventDownloadMutation,
    );

  const [addWatermark] =
    useMutation<useNodeSecurity_addWatermark_Mutation>(addWatermarkMutation);
  const [removeWatermark] =
    useMutation<useNodeSecurity_removeWatermark_Mutation>(
      removeWatermarkMutation,
    );

  return {
    syncSecuritySettings: (
      {
        isDownloadPrevented,
        isWatermarkAdded,
      }: { isDownloadPrevented: boolean; isWatermarkAdded: boolean },
      syncedIds: string[],
    ) => {
      if (isDownloadPrevented) {
        preventDownload({ variables: { nodeIds: syncedIds } });
      } else {
        allowDownload({ variables: { nodeIds: syncedIds } });
      }

      if (isWatermarkAdded) {
        addWatermark({ variables: { nodeIds: syncedIds } });
      } else {
        removeWatermark({ variables: { nodeIds: syncedIds } });
      }
    },
  };
}
